import React, { useEffect, useState, useRef } from "react";
import api, { API_BASE } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Warning, Spinner, Clock, Trash, FileArrowDown, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  pending: { color: "bg-muted text-muted-foreground", icon: Clock },
  running: { color: "bg-accent/10 text-accent", icon: Spinner },
  success: { color: "bg-primary/10 text-primary", icon: CheckCircle },
  failed: { color: "bg-destructive/10 text-destructive", icon: Warning },
};

export default function Jobs() {
  const { t } = useLang();
  const [jobs, setJobs] = useState([]);
  const timer = useRef();

  const load = async () => {
    const { data } = await api.get("/jobs");
    setJobs(data);
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, []);

  const download = async (jobId, filename) => {
    const token = localStorage.getItem("cl_token") || "";
    const orgId = localStorage.getItem("cl_active_org") || "";
    const res = await fetch(`${API_BASE}/jobs/${jobId}/download`, {
      credentials: "include",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(orgId ? { "X-Org-Id": orgId } : {}),
      },
    });
    if (!res.ok) return toast.error("Download failed");
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename || "download";
    link.click();
  };

  const remove = async (id) => {
    await api.delete(`/jobs/${id}`);
    load();
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Background queue</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("jobs")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("jobsDesc")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="jobs-refresh">
          <ArrowClockwise size={16} className="mr-2" /> Refresh
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground text-sm">{t("noJobs")}</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map((j) => {
            const meta = STATUS_META[j.status] || STATUS_META.pending;
            const Icon = meta.icon;
            const isPdf = j.type === "export_pdf";
            const isImport = j.type === "import";
            return (
              <Card key={j.id} data-testid={`job-row-${j.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-mono-data uppercase px-2 py-0.5 rounded-sm ${meta.color}`}>
                          <Icon size={12} weight={j.status === "running" ? "regular" : "fill"} className={j.status === "running" ? "animate-spin" : ""} />
                          {j.status}
                        </span>
                        <span className="text-sm font-medium capitalize">{j.type.replace("_", " ")}</span>
                        <span className="text-xs text-muted-foreground font-mono-data">{j.created_at?.slice(0, 19).replace("T", " ")}</span>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground truncate">{j.message}</div>
                      {j.status === "running" && (
                        <div className="mt-2 h-1.5 bg-secondary rounded-sm overflow-hidden">
                          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${j.progress}%` }} />
                        </div>
                      )}
                      {j.status === "success" && isImport && j.result && (
                        <div className="mt-2 text-xs font-mono-data">
                          <span className="text-primary">✓ {j.result.inserted} inserted</span>
                          {j.result.errors_count > 0 && <span className="text-destructive ml-3">✗ {j.result.errors_count} errors</span>}
                          <span className="text-muted-foreground ml-3">total {j.result.total_rows}</span>
                        </div>
                      )}
                      {j.error && (
                        <div className="mt-2 text-xs text-destructive font-mono-data">Error: {j.error}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {j.status === "success" && isPdf && (
                        <Button size="sm" variant="outline" onClick={() => download(j.id, `report_${j.meta?.year || "all"}.pdf`)} data-testid={`job-download-${j.id}`}>
                          <FileArrowDown size={14} className="mr-1" /> PDF
                        </Button>
                      )}
                      <button onClick={() => remove(j.id)} className="text-muted-foreground hover:text-destructive" data-testid={`job-delete-${j.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
